/* eslint-disable max-len */
import { InferenceScoresData } from '@/api/workOrders/types';
import {
  Box,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

import {
  BoldCell,
  CellContent,
  CellHeader,
  RowHeader,
  StyledTable,
} from '../../styles';

interface ScoresProps {
  scores: InferenceScoresData;
}

interface ScoresTable {
  item: number;
  description: string;
  third: string;
  second: string;
  first: string;
  point: number;
}

interface TableValues {
  label: string;
  value: string;
}

const presentScoresData = (data: number[]): ScoresTable[] => {
  return [
    {
      item: 1,
      description: 'Caracterização do imóvel avaliando',
      third: 'Completa quanto a todos os fatores analisados',
      second: 'Completa quanto aos fatores utilizados no modelo',
      first: 'Adoção de situação paradigma',
      point: data[0] || 0,
    },
    {
      item: 2,
      description:
        'Quantidade mínima de dados de mercado, efetivamente utilizados',
      third: '6 (k+1), onde k é o número de variáveis independentes',
      second: '4 (k+1), onde k é o número de variáveis independentes',
      first: '3 (k+1), onde k é o número de variáveis independentes',
      point: data[1] || 0,
    },
    {
      item: 3,
      description: 'Identificação dos dados de mercado',
      third:
        'Apresentação de informações relativas a todos os dados e variáveis analisados na modelagem, com foto e características observadas pelo autor do relatório',
      second:
        'Apresentação de informações relativas a todos os dados e variáveis analisados na modelagem',
      first:
        'Apresentação de informações relativas aos dados e variáveis efetivamente utilizados no modelo',
      point: data[2] || 0,
    },
    {
      item: 4,
      description: 'Extrapolação',
      third: 'Não admitida',
      second:
        'Admitida para apenas uma variável, desde que: a) as medidas das características do imóvel avaliando não sejam superiores a 100% do limite amostral superior, nem inferiores à metade do limite amostral inferior, b) o valor estimado não ultrapasse 15% do valor calculado no limite da fronteira amostral, para a referida variável',
      first:
        'Admitida, desde que: a) as medidas das características do imóvel avaliando não sejam superiores a 100% do limite amostral superior, nem inferiores à metade do limite amostral inferior b) o valor estimado não ultrapasse 20% do valor calculado no limite da fronteira amostral, para as referidas variáveis, de per si e simultaneamente, e em módulo',
      point: data[3] || 0,
    },
    {
      item: 5,
      description:
        'Nível de significância (somatório do valor das duas caudas) máximo para a rejeição da hipótese nula de cada regressor (teste bicaudal)',
      third: '10%',
      second: '20%',
      first: '30%',
      point: data[4] || 0,
    },
    {
      item: 6,
      description:
        'Nível de significância máximo admitido para a rejeição da hipótese nula do modelo através do teste F de Snedecor',
      third: '1%',
      second: '2%',
      first: '5%',
      point: data[5] || 0,
    },
  ];
};

const presentDegreesHeader = (): TableValues[] => {
  return [
    { label: '1', value: 'GRAUS' },
    { label: '2', value: 'III' },
    { label: '3', value: 'II' },
    { label: '4', value: 'I' },
    { label: '5', value: 'SOMA' },
  ];
};

const presentFundamentationText = (): string =>
  `Conforme ABNT NBR 14653-2 2011, item 9.2.1.1: Para atingir o Grau III, é obrigatório: a) apresentação do relatório na modalidade completa.`;

export function Scores({ scores }: ScoresProps): JSX.Element {
  const scoresTableData = presentScoresData(scores.item_points);
  const degreesHeaders = presentDegreesHeader();

  return (
    <Box id="inf-scores">
      <TableContainer>
        <StyledTable aria-label="tabela-pontuacao">
          <TableHead>
            <RowHeader>
              <CellHeader rowSpan={2} align="center" style={{ width: '7%' }}>
                ITEM
              </CellHeader>
              <CellHeader rowSpan={2} align="center" style={{ width: '20%' }}>
                DESCRIÇÃO
              </CellHeader>
              <CellHeader align="center" colSpan={3} style={{ width: '63%' }}>
                GRAU DE FUNDAMENTAÇÃO
              </CellHeader>
              <CellHeader rowSpan={2} align="center" style={{ width: '10%' }}>
                PONTOS OBTIDOS
              </CellHeader>
            </RowHeader>
            <RowHeader>
              <CellHeader align="center" style={{ width: '21%' }}>
                III
              </CellHeader>
              <CellHeader align="center" style={{ width: '21%' }}>
                II
              </CellHeader>
              <CellHeader align="center" style={{ width: '21%' }}>
                I
              </CellHeader>
            </RowHeader>
          </TableHead>
          <TableBody>
            {scoresTableData.map((row) => (
              <TableRow key={row.item}>
                <BoldCell align="center">{row.item}</BoldCell>
                <CellContent align="center">{row.description}</CellContent>
                <CellContent align="center" style={{ fontSize: '14px' }}>
                  {row.third}
                </CellContent>
                <CellContent align="center" style={{ fontSize: '14px' }}>
                  {row.second}
                </CellContent>
                <CellContent align="center" style={{ fontSize: '14px' }}>
                  {row.first}
                </CellContent>
                <BoldCell align="center">
                  <Typography fontWeight={600}>{row.point || '-'}</Typography>
                </BoldCell>
              </TableRow>
            ))}
          </TableBody>
        </StyledTable>
      </TableContainer>
      <TableContainer sx={{ mt: 3 }}>
        <StyledTable aria-label="tabela-graus-fundamentacao">
          <TableHead>
            <RowHeader>
              {degreesHeaders.map((header) => (
                <CellHeader align="center" key={header.label}>
                  {header.value}
                </CellHeader>
              ))}
            </RowHeader>
          </TableHead>
          <TableBody>
            <TableRow>
              <CellContent align="center">
                <Typography fontWeight={600}>Pontos Mínimos</Typography>
              </CellContent>
              <CellContent align="center">
                <Typography>16</Typography>
              </CellContent>
              <CellContent align="center">
                <Typography>10</Typography>
              </CellContent>
              <CellContent align="center">
                <Typography>6</Typography>
              </CellContent>
              <BoldCell align="center" rowSpan={2}>
                <Typography fontWeight={600}>
                  {scores.total_points ?? '-'}
                </Typography>
              </BoldCell>
            </TableRow>
            <TableRow>
              <CellContent align="center">
                <Typography fontWeight={600} sx={{ mt: 1 }}>
                  Itens Obrigatórios
                </Typography>
              </CellContent>
              <CellContent align="center">
                <Typography sx={{ mt: 1, fontSize: '14px' }}>
                  2, 4, 5 e 6 no grau III e os demais no mínimo grau II
                </Typography>
              </CellContent>
              <CellContent align="center">
                <Typography sx={{ mt: 1, fontSize: '14px' }}>
                  2, 4, 5 e 6 no mínimo no grau II e os demais no grau I
                </Typography>
              </CellContent>
              <CellContent align="center">
                <Typography sx={{ mt: 1, fontSize: '14px' }}>
                  Todos no mínimo no grau I
                </Typography>
              </CellContent>
            </TableRow>
          </TableBody>
        </StyledTable>
      </TableContainer>
      <TableContainer sx={{ mt: 3 }}>
        <StyledTable aria-label="tabela-grau-fundamentacao-relatorio">
          <TableHead>
            <RowHeader>
              <CellHeader align="center" style={{ width: '70%' }}>
                Grau de Fundamentação do Relatório
              </CellHeader>
              <CellHeader align="center" style={{ width: '30%' }}>
                <Typography fontWeight={600}>
                  {scores.reasoning_level || '-'}
                </Typography>
              </CellHeader>
            </RowHeader>
          </TableHead>
          <TableBody>
            <TableRow>
              <CellContent align="center" colSpan={2}>
                <Typography variant="body2">
                  {presentFundamentationText()}
                </Typography>
              </CellContent>
            </TableRow>
          </TableBody>
        </StyledTable>
      </TableContainer>
      <TableContainer sx={{ mt: 3 }}>
        <StyledTable aria-label="tabela-grau-precisao">
          <TableHead>
            <RowHeader>
              <CellHeader rowSpan={2} align="center" style={{ width: '25%' }}>
                DESCRIÇÃO
              </CellHeader>
              <CellHeader align="center" colSpan={3} style={{ width: '50%' }}>
                GRAU DE PRECISÃO
              </CellHeader>
              <CellHeader rowSpan={2} align="center" style={{ width: '25%' }}>
                PONTOS OBTIDOS
              </CellHeader>
            </RowHeader>
            <RowHeader>
              <CellHeader align="center">III</CellHeader>
              <CellHeader align="center">II</CellHeader>
              <CellHeader align="center">I</CellHeader>
            </RowHeader>
          </TableHead>
          <TableBody>
            <TableRow>
              <CellContent align="center">
                <Typography sx={{ fontSize: '14px' }}>
                  Amplitude do intervalo de confiança de 80% em torno da
                  estimativa de tendência central
                </Typography>
              </CellContent>
              <CellContent align="center">
                <Typography sx={{ fontSize: '14px' }}>≤ 30%</Typography>
              </CellContent>
              <CellContent align="center">
                <Typography sx={{ fontSize: '14px' }}>≤ 40%</Typography>
              </CellContent>
              <CellContent align="center">
                <Typography sx={{ fontSize: '14px' }}>≤ 50%</Typography>
              </CellContent>
              <BoldCell align="center">
                <Typography fontWeight={600}>
                  {scores.precision_level || '-'}
                </Typography>
              </BoldCell>
            </TableRow>
          </TableBody>
        </StyledTable>
      </TableContainer>
    </Box>
  );
}
